import React, { useState, useEffect, useRef } from 'react';
import api from '../../api';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import SuccessModal from '../../components/common/SuccessModal';
import FailModal from '../../components/common/FailModal';
import DateRangeFilter from '../../components/common/DateRangeFilter';
import { useSelection } from '../../hooks/useSelection';
import { validateField } from '../../utils/validation';
import { GenericView, SearchInput } from '../../components/common/table';
import { usePagination } from '../../hooks/usePagination';
import { useAuth } from '../../contexts/AuthProvider';

const SchemeCategory = () => {
  // Actions dropdown refs for manual Bootstrap Dropdown initialization
  const actionsToggleRef = useRef(null);
  const actionsContainerRef = useRef(null);
  const actionsDropdownInstanceRef = useRef(null);
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  useEffect(() => {
    const toggleEl = actionsToggleRef.current;
    const containerEl = actionsContainerRef.current;
    if (!toggleEl || !containerEl) return;
    if (typeof window !== 'undefined' && window.bootstrap?.Dropdown) {
      const instance = new window.bootstrap.Dropdown(toggleEl, { autoClose: true });
      actionsDropdownInstanceRef.current = instance;
      const onShow = () => setIsActionsOpen(true);
      const onHide = () => setIsActionsOpen(false);
      containerEl.addEventListener('show.bs.dropdown', onShow);
      containerEl.addEventListener('hide.bs.dropdown', onHide);
      return () => {
        containerEl.removeEventListener('show.bs.dropdown', onShow);
        containerEl.removeEventListener('hide.bs.dropdown', onHide);
        try { instance.dispose(); } catch {}
        actionsDropdownInstanceRef.current = null;
      };
    }
  }, []);

  // Permission system
  const { user, can, capabilitiesLoading } = useAuth();
  const canSchemeCategory = (key) => can(key, 'schemeCategory');

    // Pagination
    const pagination = usePagination(10);
    const [schemeCategory, setschemeCategory] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [search, setSearch] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');

    const handleSearchInputChange = (value) => {
        setSearch(value);
        setSearchTerm(value);
    };

    const handleSearchSubmit = () => {
        setAppliedSearchTerm(searchTerm);
        pagination.setPage(0);
    };

    // Date filter
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    // Selection
    const {
        handleCheckboxClick,
        handleSelectAll
    } = useSelection();

    // Data and form state
    const [formData, setFormData] = useState({ name: '' });
    const [editingId, setEditingId] = useState(null);
    const [errors, setErrors] = useState({});
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [confirmAction, setConfirmAction] = useState(null);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showFailModal, setShowFailModal] = useState(false);
    const [failMessage, setFailMessage] = useState('');

    const fetchSchemeCategories = async (searchValue = appliedSearchTerm) => {
        try {
            const response = await api.get(`/md/company-certscheme-page`, {
                params: {
                    page: pagination.page,
                    size: pagination.size,
                    search: searchValue,
                    from: fromDate,
                    to: toDate
                }
            });
            setschemeCategory(response.data.content);
            pagination.setTotalPages(response.data.totalPages);
            pagination.setTotal(response.data.totalElements);
            pagination.setCurrentPageItems(response.data.content.length);
        } catch (error) {
            console.error('Error fetching scheme categories:', error);
            let errorMessage = 'Failed to fetch scheme categories.';
            let technicalDetails = '';

            if (error.response) {
                switch (error.response.status) {
                    case 403:
                        errorMessage = 'You do not have permission to view scheme categories.';
                        technicalDetails = 'Required role or permission is missing';
                        break;
                    case 500:
                        errorMessage = 'Server error while fetching scheme categories.';
                        technicalDetails = error.response.data.message || 'Internal server error occurred';
                        break;
                    default:
                        errorMessage = 'An error occurred while fetching scheme categories.';
                        technicalDetails = `Server responded with status: ${error.response.status}`;
                }
            } else if (error.request) {
                errorMessage = 'Unable to connect to the server.';
                technicalDetails = 'Network error or server is not responding';
            } else {
                errorMessage = 'An unexpected error occurred.';
                technicalDetails = error.message;
            }

            setschemeCategory([]);
            setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
            setShowFailModal(true);
        }
    };

    useEffect(() => {
        fetchSchemeCategories();
    }, [pagination.page, pagination.size, fromDate, toDate, appliedSearchTerm]);

    // Handle adding a new cscheme
    const handleAddCscheme = async () => {
        if (formData.name.trim() === '') return;

        try {
            const newCscheme = {
                name: formData.name,
            };
            const response = await api.post('/md/company-certscheme', newCscheme);
            setschemeCategory([...schemeCategory, response.data]);
            setFormData({ name: '' });
        } catch (error) {
            console.error('Error adding cschema', error);
        }
    };

    // Handle editing a cscheme
    const handleEditClick = (cscheme) => {
        setEditingId(cscheme.id);
        setFormData({ name: cscheme.name });
        const modal = new window.bootstrap.Modal(document.getElementById('modal-scheme-category'));
        modal.show();
    };

    const handleUpdatecscheme = async () => {
        try {
            const updatedcscheme = {
                id: editingId,
                name: formData.name,
            };
            await api.put(`/md/company-certscheme/${editingId}`, updatedcscheme);

            // Update the cscheme in the local state
            setschemeCategory(
                schemeCategory.map((item) => (item.id === editingId ? { ...item, name: formData.name } : item))
            );
            setEditingId(null);
            setFormData({ name: '' });
        } catch (error) {
            console.error('Error updating cscheme', error);
        }
    };

    // Handle deleting a cscheme
    const handleDeletecscheme = async (id) => {
        try {
            await api.delete(`/md/company-certscheme/${id}`);
            setschemeCategory(schemeCategory.filter((cscheme) => cscheme.id !== id));

        } catch (error) {
            console.error('Error deleting cscheme', error);
        }
    };

    // Fetch schemeCategory when component mounts
    useEffect(() => {
        fetchSchemeCategories();

        // Modal events for clearing input
        const modal = document.getElementById('modal-scheme-category');
        modal?.addEventListener('hidden.bs.modal', () => {
            setFormData({ name: '' });
        });

        return () => {
            modal?.removeEventListener('hidden.bs.modal', () => { });
        };
    }, []);

    const toggleSelection = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const toggleSelectAll = () => {
        if (selectedIds.length === schemeCategory.length) {
            setSelectedIds([]);
        } else {
            const allIds = schemeCategory.map((cscheme) => cscheme.id);
            setSelectedIds(allIds);
        }
    };

    const handleCheckboxChange = (id) => {
        setSelectedIds((prevSelected) =>
            prevSelected.includes(id)
                ? prevSelected.filter((selectedId) => selectedId !== id)
                : [...prevSelected, id]
        );
    };

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) {
            alert("Pilih minimal satu data yang ingin dihapus.");
            return;
        }

        try {
            await api.delete('/md/company-certscheme/bulk', { data: selectedIds }); // sesuaikan endpoint
            fetchSchemeCategories(); // refresh data
            setSelectedIds([]); // reset pilihan
        } catch (err) {
            console.error("Error deleting selected schemeCategory:", err);
        }
    };

    const handleDeleteSelected = async () => {
        if (selectedIds.length === 0) {
            alert('Pilih data yang ingin dihapus.');
        }
        return;

        if (!window.confirm('Yakin ingin menghapus data yang dipilih?')) return;

        try {
            await Promise.all(
                selectedIds.map((id) => api.delete(`/md/company-certscheme/${id}`))
            );

            // Update state setelah delete
            setschemeCategory(schemeCategory.filter((cscheme) => !selectedIds.includes(cscheme.id)));
            setSelectedIds([]);
        } catch (error) {
            console.error('Error deleting selected schemeCategory:', error);
        }
    };

    // Open modal for create
    const handleCreate = () => {
    if (!canSchemeCategory('create')) { setFailMessage('You do not have permission to create.'); setShowFailModal(true); return; }
        setFormData({ name: '' });
        setEditingId(null);
        setErrors({});
        const modal = new window.bootstrap.Modal(document.getElementById('modal-scheme-category'));
        modal.show();
    };

    // Open modal for edit
    const handleEdit = (item) => {
    if (!canSchemeCategory('edit')) { setFailMessage('You do not have permission to edit.'); setShowFailModal(true); return; }
        setFormData({ name: item.name });
        setEditingId(item.id);
        setErrors({});
        const modal = new window.bootstrap.Modal(document.getElementById('modal-scheme-category'));
        modal.show();
    };

    // Real-time validation
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        let error = null;
        if (name === 'name') {
            error = validateField('name', value, { required: true });
        }
        setErrors((prev) => ({ ...prev, [name]: error }));
    };

    // Unified handleSubmit for both create and edit
    const handleSubmit = async (e) => {
        e.preventDefault();
        const error = validateField('name', formData.name, { required: true });
        if (error) {
            setErrors({ name: error });
            return;
        }
        try {
            if (editingId) {
                await api.put(`/md/company-certscheme/${editingId}`, { id: editingId, name: formData.name });
                setSuccessMessage('Scheme category has been successfully updated.');
            } else {
                await api.post('/md/company-certscheme', { name: formData.name });
                setSuccessMessage('Scheme category has been successfully added.');
            }
            setShowSuccessModal(true);
            setEditingId(null);
            setFormData({ name: '' });
            setErrors({});
            await fetchSchemeCategories();
            const modal = document.getElementById('modal-scheme-category');
            const bootstrapModal = window.bootstrap?.Modal.getInstance(modal);
            if (bootstrapModal) bootstrapModal.hide();
        } catch (error) {
            console.error('Error saving scheme category:', error);
            let errorMessage = 'Failed to save scheme category.';
            let technicalDetails = '';

            if (error.response) {
                switch (error.response.status) {
                    case 409:
                        errorMessage = 'A scheme category with this name already exists.';
                        technicalDetails = `Duplicate entry for name: "${formData.name}"`;
                        break;
                    case 400:
                        errorMessage = 'Invalid data provided.';
                        technicalDetails = error.response.data.message || 'The server rejected the request due to invalid data';
                        break;
                    case 403:
                        errorMessage = 'You do not have permission to perform this action.';
                        technicalDetails = 'Required role or permission is missing for this operation';
                        break;
                    case 404:
                        errorMessage = 'Scheme category not found.';
                        technicalDetails = 'The requested item may have been deleted or does not exist';
                        break;
                    case 500:
                        errorMessage = 'Server encountered an error while processing the request.';
                        technicalDetails = error.response.data.message || 'Internal server error occurred';
                        break;
                    default:
                        errorMessage = 'An error occurred while saving the scheme category.';
                        technicalDetails = `Server responded with status: ${error.response.status}`;
                }
            } else if (error.request) {
                errorMessage = 'Unable to connect to the server.';
                technicalDetails = 'Network error or server is not responding';
            } else {
                errorMessage = 'An unexpected error occurred.';
                technicalDetails = error.message;
            }

            setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
            setShowFailModal(true);
        }
    };

    // Edit selected (only works for single selection)
    const editSelected = () => {
        if (selectedIds.length !== 1) {
            setSuccessMessage('Please select exactly one scheme category to edit');
            setShowSuccessModal(true);
            return;
        }
        const selectedId = selectedIds[0];
        const selectedItem = schemeCategory.find(item => item.id === selectedId);
        if (selectedItem) {
            handleEdit(selectedItem);
        }
    };

    // Delete selected (bulk)
    const deleteSelected = () => {
    if (!canSchemeCategory('delete')) { setFailMessage('You do not have permission to delete.'); setShowFailModal(true); return; }
        if (selectedIds.length === 0) {
            setFailMessage('Please select at least one scheme category to delete.\n\nTechnical Details: No items selected for deletion');
            setShowFailModal(true);
            return;
        }
        setConfirmAction(() => async () => {
            try {
                await api.delete('/md/company-certscheme/bulk', { data: selectedIds });
                setSelectedIds([]);
                fetchSchemeCategories();
                setShowDeleteConfirmation(false);
                setSuccessMessage('Selected scheme categories deleted successfully');
                setShowSuccessModal(true);
            } catch (error) {
                console.error('Error deleting scheme categories:', error);
                let errorMessage = 'Failed to delete scheme categories.';
                let technicalDetails = '';

                if (error.response) {
                    switch (error.response.status) {
                        case 403:
                            errorMessage = 'You do not have permission to delete these items.';
                            technicalDetails = 'Required role or permission is missing for deletion';
                            break;
                        case 400:
                            errorMessage = 'Invalid delete request.';
                            technicalDetails = error.response.data.message || 'The server rejected the deletion request';
                            break;
                        case 404:
                            errorMessage = 'One or more items not found.';
                            technicalDetails = 'Some items may have been already deleted';
                            break;
                        case 405:
                            errorMessage = 'Delete operation not allowed.';
                            technicalDetails = 'The server does not support the DELETE method for this endpoint';
                            break;
                        case 500:
                            errorMessage = 'Server error while deleting items.';
                            technicalDetails = error.response.data.message || 'Internal server error during deletion';
                            break;
                        default:
                            errorMessage = 'An error occurred while deleting the items.';
                            technicalDetails = `Server responded with status: ${error.response.status}`;
                    }
                } else if (error.request) {
                    errorMessage = 'Unable to connect to the server.';
                    technicalDetails = 'Network error or server is not responding';
                } else {
                    errorMessage = 'An unexpected error occurred during deletion.';
                    technicalDetails = error.message;
                }

                setShowDeleteConfirmation(false);
                setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
                setShowFailModal(true);
            }
        });
        setShowDeleteConfirmation(true);
    };

    return (
        <>

                {/* Page header */}
                <div className="page-header d-print-none">
                    <div className="container-xl">
                        <div className="row g-2 align-items-center">
                            <div className="col">
                                <h2 className="page-title d-flex align-items-center">
                                    <a className="me-2 d-flex align-items-center" href="./masterdata-main.html">
                                        <span className="d-flex align-items-center">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                                                strokeLinecap="round" strokeLinejoin="round"
                                                className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-left">
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                <path d="M15 6l-6 6l6 6" />
                                            </svg>
                                        </span>
                                    </a>
                                    Scheme Category
                                </h2>

                            </div>
                            <div className="col-auto ms-auto d-print-none">

                                <div className="btn-list">
                                    <a href="#" className="btn btn-primary btn-5 d-none d-sm-inline-block"
                                        onClick={handleCreate}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                            strokeLinejoin="round" className="icon icon-2">
                                            <path d="M12 5l0 14" />
                                            <path d="M5 12l14 0" />
                                        </svg>
                                        Create
                                    </a>
                                    <a href="#" className="btn btn-primary btn-6 d-sm-none btn-icon"
                                        onClick={handleCreate} aria-label="Create new report">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                            strokeLinejoin="round" className="icon icon-2">
                                            <path d="M12 5l0 14" />
                                            <path d="M5 12l14 0" />
                                        </svg>
                                    </a>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>

                <div className="page-body">
                    <div className="container-xl">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    <GenericView
                                        data={schemeCategory}
                                        columns={[
                                            { key: 'name', label: 'Name', sortable: true },
                                            { key: 'cuser', label: 'Created By', sortable: true, render: (value, row) => row.cuser?.username || '-' },
                                            { key: 'cdate', label: 'Created Date', sortable: true, render: (value) => value ? new Date(value).toLocaleString() : '-' },
                                        ]}
                                        isLoading={capabilitiesLoading}
                                        emptyStateMessage="No scheme categories found"
                                        checkboxSelection={true}
                                        selectedRows={selectedIds}
                                        onSelectRow={(itemId, isSelected) => {
                                            if (isSelected) {
                                                setSelectedIds([...selectedIds, itemId]);
                                            } else {
                                                setSelectedIds(selectedIds.filter(id => id !== itemId));
                                            }
                                        }}
                                        onSelectAllRows={(itemIds) => {
                                            if (Array.isArray(itemIds) && itemIds.length > 0) {
                                                setSelectedIds([...new Set([...selectedIds, ...itemIds])]);
                                            } else {
                                                setSelectedIds([]);
                                            }
                                        }}
                                        onEdit={canSchemeCategory('edit') ? (item) => {
                                            setSelectedIds([item.id]);
                                            handleEdit(item);
                                        } : undefined}
                                        onDelete={canSchemeCategory('delete') ? (item) => {
                                            setSelectedIds([item.id]);
                                            deleteSelected();
                                        } : undefined}
                                        searchComponent={
                                            <div className="d-flex gap-2">
                                                <SearchInput
                                                    value={searchTerm}
                                                    onChange={handleSearchInputChange}
                                                    onKeyPress={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            handleSearchSubmit();
                                                        }
                                                    }}
                                                    placeholder="Search..."
                                                    uniqueId="scheme-category-search"
                                                />
                                                <button type="button" className="btn btn-primary" onClick={handleSearchSubmit}>
                                                    Search
                                                </button>
                                            </div>
                                        }
                                        actionButtons={
                                            <div className="d-flex gap-2">
                                                {(canSchemeCategory('edit') || canSchemeCategory('delete')) && (
                                                    <div ref={actionsContainerRef} className="dropdown">
                                                        <a
                                                            ref={actionsToggleRef}
                                                            href="#"
                                                            className="btn dropdown-toggle"
                                                            data-bs-toggle="dropdown"
                                                            aria-expanded={isActionsOpen}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                const inst = actionsDropdownInstanceRef.current;
                                                                if (inst?.toggle) inst.toggle();
                                                                else setIsActionsOpen(prev => !prev);
                                                            }}
                                                        >Actions</a>
                                                        <div className="dropdown-menu">
                                                            {canSchemeCategory('edit') && (
                                                                <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); editSelected(); }}
                                                                    style={{ color: selectedIds.length === 1 ? "" : "#ccc", pointerEvents: selectedIds.length === 1 ? "auto" : "none" }}>
                                                                    Edit
                                                                </a>
                                                            )}
                                                            {canSchemeCategory('delete') && (
                                                                <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); deleteSelected(); }}
                                                                    style={{ color: selectedIds.length ? "" : "#ccc", pointerEvents: selectedIds.length ? "auto" : "none" }}>
                                                                    Delete
                                                                </a>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        }
                                        pagination={{
                                            currentPage: pagination.page + 1,
                                            totalRows: pagination.total,
                                            rowsPerPage: pagination.size,
                                            onPageChange: (page) => pagination.setPage(page - 1),
                                            onRowsPerPageChange: (size) => {
                                                pagination.setSize(size);
                                                pagination.setPage(0);
                                            }
                                        }}
                                        pageSizeOptions={[10, 25, 50, 100]}
                                        uniqueId="scheme-category-table"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            

            {/* Modal Add cscheme */}
            <div className="modal modal-blur fade" id="modal-scheme-category" tabIndex="-1" role="dialog" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{editingId ? 'Edit Scheme Category' : 'Add New Scheme Category'}</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Name <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                        name="name"
                                        placeholder="Enter Scheme Category"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                    />
                                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <a href="#" className="btn btn-link link-secondary btn-3" data-bs-dismiss="modal">Cancel</a>
                                <button type="submit" className="btn btn-primary ms-auto" id="createBtn">
                                    {editingId ? 'Update' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={showDeleteConfirmation}
                onClose={() => setShowDeleteConfirmation(false)}
                onConfirm={confirmAction || (() => { })}
                title="Delete Confirmation"
                message={selectedIds.length > 0
                    ? "Are you sure you want to delete the selected scheme categories? This action cannot be undone."
                    : "Are you sure you want to delete this scheme category? This action cannot be undone."}
                confirmText="Delete"
                cancelText="Cancel"
                type="danger"
            />

            {/* Success Modal */}
            <SuccessModal
                isOpen={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                title="Success"
                message={successMessage}
                buttonText="OK"
            />

            {/* Fail Modal */}
            <FailModal
                isOpen={showFailModal}
                onClose={() => setShowFailModal(false)}
                title="Error"
                message={failMessage}
                buttonText="OK"
            />
        </>
    );
};

export default SchemeCategory;

